import { FiStar, FiChevronDown } from 'react-icons/fi';

const formatDate = (raw) =>
  new Date(raw).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const isRealIsbn = (v, placeholder) => !!v && String(v) !== placeholder;

const formatWeight = (raw) => {
  const g = Number(raw);
  if (!Number.isFinite(g) || g <= 0) return null;
  return `${g} gr`;
};

const Row = ({ label, children }) => (
  <li className="flex flex-wrap gap-x-2 ">
    <span className="font-bold text-black">{label}</span>
    <span className="text-black">:</span>
    <span className="text-black break-words">{children}</span>
  </li>
);

const BookDetails = ({ book }) => {
  const weightLabel = formatWeight(book.weight);
  const publicationDate = book.publisherDate;
  const readingAge = String(book.readingAge ?? '').trim();

  return (
    <section className="mt-8 border-y border-gray-200 px-4 py-4">
      <h2 className="text-xl font-bold text-red-600 mb-2">Product Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 text-sm text-black">
        <ul className="list-none m-0 p-0 md:pr-8 space-y-1.5">
          {isRealIsbn(book.isbn13, '9780000000000') && <Row label="ISBN-13">{book.isbn13}</Row>}
          {book.publisher && <Row label="Publisher">{book.publisher}</Row>}
          {book.height > 0 && <Row label="Height">{Number(book.height)} mm</Row>}
          {book.pages > 0 && <Row label="No of Pages">{book.pages}</Row>}
          {weightLabel && <Row label="Weight">{weightLabel}</Row>}
        </ul>
        <ul className="list-none m-0 p-0 mt-3 pt-3 border-t border-gray-200 md:mt-0 md:pt-0 md:pl-8 md:border-t-0 md:border-l space-y-1.5">
          {isRealIsbn(book.isbn10, '0000000000') && <Row label="ISBN-10">{book.isbn10}</Row>}
          {publicationDate && <Row label="Publisher Date">{formatDate(publicationDate)}</Row>}
          {book.binding && <Row label="Binding">{book.binding}</Row>}
          <Row label="Reading age">{readingAge || 'Not specified'}</Row>
          {book.language && <Row label="Language">{book.language}</Row>}
          <Row label="Returnable">{book.returnable ? 'Y' : 'N'}</Row>
          {book.spineWidth > 0 && <Row label="Spine Width">{Number(book.spineWidth)} mm</Row>}
          {book.width > 0 && <Row label="Width">{Number(book.width)} mm</Row>}
        </ul>
      </div>
    </section>
  );
};

const NonBookSpecs = ({ book, avgRating, reviewCount, categoryName }) => {
  const ratingsLabel = (book.reviewCount || reviewCount || 0).toLocaleString('en-IN');
  return (
    <section className="mt-8 border-t border-gray-200 pt-6">
      <h2 className="text-xl font-bold text-black mb-3">Product information</h2>
      <ul className="list-none m-0 p-0 pl-2 text-sm text-black max-w-3xl space-y-1.5">
        {Object.entries(book.specs).map(([k, v]) => (
          <Row key={k} label={k}>{String(v)}</Row>
        ))}
        {book.netQuantity && <Row label="Net Quantity">{book.netQuantity}</Row>}
        {book.isBestseller && (
          <li className="pt-2">
            <span className="font-bold text-black">Best Sellers Rank:</span>{' '}
            <span className="text-black">#1 in {categoryName}</span>
          </li>
        )}
        <li className="pt-2 flex items-center gap-1 flex-wrap">
          <span className="font-bold text-black">Customer Reviews:</span>
          <span className="text-black">{avgRating}</span>
          <span className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <FiStar
                key={i}
                className={`text-sm ${i < Math.round(Number(avgRating))
                  ? 'text-orange-400 fill-orange-400'
                  : 'text-black'
                  }`}
              />
            ))}
          </span>
          <FiChevronDown className="text-black" />
          <a href="#reviews" className="text-blue-700 hover:text-orange-600 hover:underline">
            ({ratingsLabel})
          </a>
        </li>
      </ul>
    </section>
  );
};

const AboutAuthor = ({ book }) => {
  return (
    <section className="pt-6">
      <h2 className="text-xl font-bold text-black mb-3">About the Author</h2>
      <p className="text-sm text-black leading-7 text-justify whitespace-pre-line">
        {book.authorBio}
      </p>
    </section>
  );
};

const ProductDetailsBlock = ({ book, isBook, avgRating, reviewCount, categoryName }) => {
  const hasSpecs = !!book.specs && Object.keys(book.specs).length > 0;
  const showBookLayout = isBook || !hasSpecs;
  return (
    <>
      {showBookLayout && book.authorBio?.trim() && <AboutAuthor book={book} />}
      {showBookLayout ? (
        <BookDetails book={book} />
      ) : (
        <NonBookSpecs
          book={book}
          avgRating={avgRating}
          reviewCount={reviewCount}
          categoryName={categoryName}
        />
      )}
    </>
  );
};

export default ProductDetailsBlock;
